import {
  ApplicationRef,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  output,
  signal,
  untracked,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { MEMBERS_DATA_SERVICE } from '@app/app/page-modules/admin/services/members/members-data.service';
import { MembersReIndexScope, MembersReIndexStatus } from '@app/app/shared/models/admin/members.model';
import { ButtonComponent, ButtonSizes } from '@app/app/shared/styleguide/components/buttons/default/button.component';
import { StyleguideModule } from '@app/app/shared/styleguide/styleguide.module';
import { UserAuthState } from '@app/app/user-auth/store/user-auth.state';
import { LucideRefreshCw } from '@lucide/angular';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { Store } from '@ngxs/store';
import { Subscription, switchMap, take, timer } from 'rxjs';
import { TOAST_NOTIFICATION_SERVICE } from 'src/app/shared/services/toast-notifications/toast-service';

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 600;

@Component({
  selector: 'ptl-members-re-index-button',
  exportAs: 'ptlMembersReIndex',
  host: { '[style.display]': "appearance() === 'headless' ? 'none' : null" },
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslocoModule, StyleguideModule, ButtonComponent],
  template: `
    @if (appearance() === 'v3' && enabled() && scope(); as currentScope) {
      <ptl-tooltip [text]="tooltip()">
        <ptl-button
          [type]="'secondary-neutral'"
          [size]="size()"
          [icon]="LucideRefreshCw"
          [text]="'translations.membersNew.reIndex.button' | transloco"
          [loading]="syncing()"
          [disabled]="syncing()"
          [attr.aria-label]="'translations.membersNew.reIndex.button' | transloco"
          (clicked)="start(currentScope)"
        ></ptl-button>
      </ptl-tooltip>
    }
  `,
})
export class MembersReIndexButtonComponent {
  playlistUid = input<string | null | undefined>(null);
  cardUid = input<string | null | undefined>(null);
  size = input<ButtonSizes>('sm');
  appearance = input<'v3' | 'headless'>('v3');

  completed = output<number>();

  protected readonly LucideRefreshCw = LucideRefreshCw;
  readonly syncing = signal(false);
  readonly enabled = toSignal(inject(Store).select(UserAuthState.membersReIndexButtonFeatureFlagEnabled), { initialValue: false });
  readonly scope = computed<MembersReIndexScope | null>(() => {
    const cardUid = this.cardUid();
    const playlistUid = this.playlistUid();
    if (cardUid) return { cardUid, playlistUid };
    return playlistUid ? { playlistUid } : null;
  });
  protected readonly lastRun = signal<MembersReIndexStatus | null>(null);
  readonly tooltip = computed(() => {
    if (this.syncing()) return this.translocoService.translate('translations.membersNew.reIndex.tooltipRunning');
    const explanation = this.translocoService.translate(
      this.cardUid() ? 'translations.membersNew.reIndex.tooltipCard' : 'translations.membersNew.reIndex.tooltipPlaylist',
    );
    const run = this.lastRun();
    if (run?.status !== 'COMPLETED' || run.memberCount == null) return explanation;
    const lastRun = this.translocoService.translate('translations.membersNew.reIndex.tooltipLastRun', { count: run.memberCount });
    return `${explanation} ${lastRun}`;
  });

  private readonly membersDataService = inject(MEMBERS_DATA_SERVICE);
  private readonly toastService = inject(ApplicationRef).injector.get(TOAST_NOTIFICATION_SERVICE);
  private readonly translocoService = inject(TranslocoService);
  private readonly destroyRef = inject(DestroyRef);
  private polling: Subscription | null = null;
  private request: Subscription | null = null;

  constructor() {
    effect(() => {
      const currentScope = this.scope();
      untracked(() => {
        this.cancelInFlight();
        if (currentScope) this.resumeIfRunning(currentScope);
      });
    });
    this.destroyRef.onDestroy(() => this.cancelInFlight());
  }

  start(scope: MembersReIndexScope | null = this.scope()): void {
    if (!scope || this.syncing()) return;
    this.syncing.set(true);
    this.request = this.membersDataService
      .startMembersReIndex(scope)
      .pipe(take(1))
      .subscribe(({ isSuccess, value, error }) => {
        if (!isSuccess) {
          this.syncing.set(false);
          this.toastService.showFail(
            error === '403' ? this.translocoService.translate('translations.membersNew.reIndex.notAllowed') : error,
          );
          return;
        }
        this.onStatus(scope, value);
      });
  }

  private cancelInFlight(): void {
    this.request?.unsubscribe();
    this.request = null;
    this.polling?.unsubscribe();
    this.polling = null;
    this.syncing.set(false);
  }

  private resumeIfRunning(scope: MembersReIndexScope): void {
    this.request = this.membersDataService
      .getMembersReIndexStatus(scope)
      .pipe(take(1))
      .subscribe(({ isSuccess, value }) => {
        if (!isSuccess) return;
        if (value.status === 'RUNNING') {
          this.syncing.set(true);
          this.poll(scope);
          return;
        }
        this.lastRun.set(value);
      });
  }

  private onStatus(scope: MembersReIndexScope, status: MembersReIndexStatus): void {
    if (status.status === 'RUNNING') {
      this.toastService.showSuccess(this.translocoService.translate('translations.membersNew.reIndex.started'));
      this.poll(scope);
      return;
    }
    this.finish(status);
  }

  private poll(scope: MembersReIndexScope): void {
    this.polling?.unsubscribe();
    this.polling = timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        take(MAX_POLLS),
        switchMap(() => this.membersDataService.getMembersReIndexStatus(scope)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ isSuccess, value }) => {
        if (!isSuccess) {
          this.polling?.unsubscribe();
          this.syncing.set(false);
          return;
        }
        if (value.status !== 'RUNNING') {
          this.polling?.unsubscribe();
          this.finish(value);
        }
      });
  }

  private finish(status: MembersReIndexStatus): void {
    this.syncing.set(false);
    if (status.status === 'FAILED') {
      this.toastService.showFail(this.translocoService.translate('translations.membersNew.reIndex.failed'));
      return;
    }
    const count = status.memberCount ?? 0;
    this.lastRun.set(status);
    const message =
      count === 0
        ? this.translocoService.translate('translations.membersNew.reIndex.successNone')
        : this.translocoService.translate('translations.membersNew.reIndex.success', { count });
    this.toastService.showSuccess(message);
    this.completed.emit(count);
  }
}
